const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY?.trim(),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim(),
  appId: import.meta.env.VITE_FIREBASE_APP_ID?.trim(),
}

const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY?.trim()
const professionalEndpoint = import.meta.env.VITE_PROFESSIONAL_ENQUIRY_ENDPOINT?.trim()
const partnershipEndpoint = import.meta.env.VITE_CONTACT_ENDPOINT?.trim()
const analyticsEndpoint = import.meta.env.VITE_PROFESSIONAL_ANALYTICS_ENDPOINT?.trim()

const appCheckReady = Boolean(
  appCheckSiteKey &&
  firebaseConfig.apiKey &&
  firebaseConfig.projectId &&
  firebaseConfig.appId,
)
export const productionProfessionalFormReady = Boolean(professionalEndpoint && appCheckReady)

export class SubmissionError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fields?: Record<string, string>,
  ) {
    super(message)
  }
}

let appCheckPromise: Promise<import('@firebase/app-check').AppCheck> | undefined

async function appCheckToken() {
  if (!appCheckReady) {
    if (import.meta.env.DEV) return undefined
    throw new Error('Firebase App Check is not configured.')
  }

  appCheckPromise ??= Promise.all([import('@firebase/app'), import('@firebase/app-check')]).then(([appModule, appCheckModule]) => {
    const app = appModule.initializeApp(firebaseConfig, 'ark-public-submissions')
    return appCheckModule.initializeAppCheck(app, {
      provider: new appCheckModule.ReCaptchaEnterpriseProvider(appCheckSiteKey!),
      isTokenAutoRefreshEnabled: true,
    })
  })

  const appCheck = await appCheckPromise
  const { getLimitedUseToken } = await import('@firebase/app-check')
  return (await getLimitedUseToken(appCheck)).token
}

async function protectedHeaders(base: HeadersInit = {}) {
  const headers = new Headers(base)
  const token = await appCheckToken()
  if (token) headers.set('X-Firebase-AppCheck', token)
  return headers
}

export async function submitProfessionalEnquiry(formData: FormData, idempotencyKey: string) {
  if (!professionalEndpoint) {
    if (import.meta.env.DEV) {
      await new Promise((resolve) => window.setTimeout(resolve, 450))
      return
    }
    throw new Error('Professional enquiry endpoint is not configured.')
  }
  const response = await fetch(professionalEndpoint, {
    method: 'POST',
    headers: await protectedHeaders({ 'X-Idempotency-Key': idempotencyKey }),
    body: formData,
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { fields?: Record<string, string> }
    throw new SubmissionError(`Professional enquiry failed with ${response.status}`, response.status, body.fields)
  }
}

export async function submitPartnershipEnquiry(payload: { email: string; firm: string; need: string }, idempotencyKey: string) {
  if (!partnershipEndpoint) throw new Error('Partnership enquiry endpoint is not configured.')
  const response = await fetch(partnershipEndpoint, {
    method: 'POST',
    headers: await protectedHeaders({
      'Content-Type': 'application/json',
      'X-Idempotency-Key': idempotencyKey,
    }),
    body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error(`Partnership enquiry failed with ${response.status}`)
}

export type ProfessionalAnalyticsEvent =
  | { event: 'professional_page_viewed' | 'enquiry_started' | 'submission_succeeded' }
  | { event: 'form_group_completed'; outcome: 'contact_details' | 'professional_profile' | 'work_preferences' | 'cv_and_permissions' }
  | { event: 'submission_failed'; outcome: 'validation' | 'app_check' | 'rate_limited' | 'server' }

export function recordProfessionalEvent(event: ProfessionalAnalyticsEvent) {
  if (!analyticsEndpoint) return
  void protectedHeaders({ 'Content-Type': 'application/json' })
    .then((headers) => fetch(analyticsEndpoint, { method: 'POST', headers, body: JSON.stringify(event), keepalive: true }))
    .catch(() => undefined)
}
